import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Lock, Search, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import MasterSelect from "./MasterSelect";

/**
 * Share Asset dialog. Two modes:
 *  - fromAsset: assetId fixed → pick brokers (suggestions ranked first), exclusivity.
 *  - fromBroker: brokerId fixed → pick an asset, then share to that single broker.
 */
export default function ShareAssetDialog({ open, onClose, assetId, brokerId }) {
  const qc = useQueryClient();
  const mode = assetId ? "fromAsset" : "fromBroker";
  const [pickedAssetId, setPickedAssetId] = useState(assetId || "");
  const [assetQuery, setAssetQuery] = useState("");
  const [selected, setSelected] = useState(brokerId ? [brokerId] : []);
  const [exclusivity, setExclusivity] = useState("Open");
  const [expiry, setExpiry] = useState("");
  const [shareMethodId, setShareMethodId] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmStale, setConfirmStale] = useState(false);

  useEffect(() => {
    if (open) {
      setPickedAssetId(assetId || "");
      setSelected(brokerId ? [brokerId] : []);
      setExclusivity("Open"); setExpiry(""); setNotes(""); setConfirmStale(false);
    }
  }, [open, assetId, brokerId]);

  const effectiveAsset = assetId || pickedAssetId;

  // Asset search (fromBroker mode)
  const { data: assetResults } = useQuery({
    queryKey: ["share-asset-search", assetQuery],
    queryFn: async () => (await api.get("/assets", { params: { q: assetQuery, limit: 8 } })).data,
    enabled: mode === "fromBroker" && open,
  });
  const assets = assetResults?.items || assetResults || [];

  // Broker suggestions (fromAsset mode)
  const { data: suggestions = [] } = useQuery({
    queryKey: ["share-suggestions", effectiveAsset],
    queryFn: async () => (await api.get("/brokers/share-suggestions", { params: { asset_id: effectiveAsset } })).data,
    enabled: mode === "fromAsset" && open && !!effectiveAsset,
  });

  const toggleBroker = (id) => {
    if (exclusivity === "Exclusive") { setSelected([id]); return; }
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const share = useMutation({
    mutationFn: async () => {
      return api.post("/brokers/share", {
        asset_id: effectiveAsset,
        broker_ids: selected,
        exclusivity_type: exclusivity,
        exclusivity_expiry: exclusivity === "Exclusive" ? expiry : null,
        share_method_id: shareMethodId || null,
        notes: notes || null,
        confirm_stale: confirmStale,
      });
    },
    onSuccess: (res) => {
      const { created, reoffered, blocked } = res.data;
      if (created.length) toast.success(`Shared with ${created.length} broker(s)`);
      if (reoffered.length) toast.info(`Re-offered to ${reoffered.length} broker(s)`);
      if (blocked.length) toast.warning(`${blocked.length} blocked: ${blocked.map((b) => b.reason).join("; ")}`);
      qc.invalidateQueries({ queryKey: ["asset-shares"] });
      qc.invalidateQueries({ queryKey: ["broker-shares"] });
      onClose();
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "Share failed";
      if (e?.response?.status === 409 && detail.toLowerCase().includes("confirm to share")) {
        setConfirmStale(true);
        toast.warning(detail);
      } else {
        toast.error(detail);
      }
    },
  });

  const canSubmit = effectiveAsset && selected.length > 0 &&
    (exclusivity !== "Exclusive" || (selected.length === 1 && expiry));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="share-asset-dialog">
        <DialogHeader><DialogTitle>Share Asset with Brokers</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {mode === "fromBroker" && (
            <div>
              <Label>Asset</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={assetQuery} onChange={(e) => setAssetQuery(e.target.value)} placeholder="Search assets…" className="pl-8" data-testid="share-asset-search" />
              </div>
              <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded">
                {assets.map((a) => (
                  <button key={a.id} onClick={() => setPickedAssetId(a.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${pickedAssetId === a.id ? "bg-slate-100 font-semibold" : ""}`}
                    data-testid={`share-asset-option-${a.id}`}>
                    {a.name} <span className="text-xs text-slate-400">{a.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Exclusivity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Exclusivity</Label>
              <div className="flex gap-2 mt-1">
                {["Open", "Exclusive"].map((t) => (
                  <button key={t} onClick={() => { setExclusivity(t); if (t === "Exclusive" && selected.length > 1) setSelected(selected.slice(0, 1)); }}
                    className={`px-3 py-1.5 rounded border text-sm ${exclusivity === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}
                    data-testid={`share-exclusivity-${t.toLowerCase()}`}>{t}</button>
                ))}
              </div>
            </div>
            {exclusivity === "Exclusive" && (
              <div>
                <Label>Exclusivity Expiry *</Label>
                <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1" data-testid="share-exclusivity-expiry" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Share Method</Label>
              <div className="mt-1"><MasterSelect listType="activity_type" value={shareMethodId} onChange={setShareMethodId} placeholder="Method…" testId="share-method" /></div>
            </div>
          </div>

          {/* Broker picker (fromAsset) or selected broker (fromBroker) */}
          {mode === "fromAsset" && (
            <div>
              <Label>Brokers {exclusivity === "Exclusive" ? "(pick one)" : "(multi-select)"}</Label>
              <div className="mt-1.5 max-h-64 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
                {suggestions.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500">No shareable brokers.</div>
                ) : suggestions.map((b) => (
                  <label key={b.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`share-broker-${b.id}`}>
                    <input type={exclusivity === "Exclusive" ? "radio" : "checkbox"} checked={selected.includes(b.id)} onChange={() => toggleBroker(b.id)} name="broker-pick" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        {b.broker_name}
                        {b.suggested && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"><Sparkles className="h-3 w-3 mr-0.5" />Suggested</Badge>}
                      </div>
                      <div className="text-[11px] text-slate-500">{b.firm_name || "—"} · {b.relationship_status_label}{b.match_reasons?.length ? ` · matches: ${b.match_reasons.join(", ")}` : ""}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" rows={2} data-testid="share-notes" />
          </div>

          {confirmStale && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="share-stale-warning">
              <AlertTriangle className="h-4 w-4" /> Asset is not Active — sharing anyway on submit.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => share.mutate()} disabled={!canSubmit || share.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="share-submit">
            {confirmStale ? "Share anyway" : "Share"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
